import PropTypes from 'prop-types';
// material
import { styled } from '@mui/material/styles';
import {
  Button,
  Toolbar,
  Tooltip,
  IconButton,
  Typography,
  OutlinedInput,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
// component
import Iconify from '../../../components/Iconify';

// ----------------------------------------------------------------------

const RootStyle = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 1, 2, 3),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2),
  },
}));

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: '100%',
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': { boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

// ----------------------------------------------------------------------

UserListToolbar.propTypes = {
  numSelected: PropTypes.number,
  filterName: PropTypes.string,
  onFilterName: PropTypes.func,
  handleSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  onCreateClick: PropTypes.func,
  meetingsValue: PropTypes.number,
  onMeetingsChange: PropTypes.func,
  meetingsOptions: PropTypes.array,
  useCaseValue: PropTypes.string,
  onUseCaseChange: PropTypes.func,
  useCaseOptions: PropTypes.array,
};

export default function UserListToolbar({
  numSelected,
  filterName,
  onFilterName,
  handleSearch,
  isLoading,
  onCreateClick,
  meetingsValue,
  onMeetingsChange,
  meetingsOptions,
  useCaseValue,
  onUseCaseChange,
  useCaseOptions,
}) {
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch({ reset: false });
    }
  };

  // Los dos modos de filtro son EXCLUYENTES (no se combinan):
  //  - Modo actividad: reuniones/mes > 0  → bloquea texto y caso de uso.
  //  - Modo texto/useCase: hay texto o caso de uso → bloquea reuniones/mes.
  const activityMode = meetingsValue > 0;
  const textMode = (filterName && filterName !== '') || (useCaseValue && useCaseValue !== '');

  return (
    <RootStyle
      sx={{
        ...(numSelected > 0 && {
          color: 'primary.main',
          bgcolor: 'primary.lighter',
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography component="div" variant="subtitle1">
          {numSelected} selected
        </Typography>
      ) : (
        <>
          {onCreateClick && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="eva:plus-outline" />}
              onClick={onCreateClick}
              sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
            >
              Crear
            </Button>
          )}
          <SearchStyle
            value={filterName}
            onChange={onFilterName}
            onKeyDown={handleKeyDown}
            disabled={activityMode}
            placeholder="Busca un usuario por nombre, id o email..."
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
              </InputAdornment>
            }
            sx={{ flex: { md: 1 } }}
          />
          {meetingsOptions && (
            <TextField
              select
              label="Reuniones/mes"
              value={meetingsValue}
              onChange={(e) => onMeetingsChange(Number(e.target.value))}
              disabled={textMode}
              helperText={textMode ? 'No se combina con búsqueda' : ' '}
              size="small"
              sx={{ minWidth: { md: 150 }, flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
            >
              {meetingsOptions.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {useCaseOptions && (
            <TextField
              select
              label="Caso de uso"
              value={useCaseValue}
              onChange={(e) => onUseCaseChange(e.target.value)}
              disabled={activityMode}
              size="small"
              sx={{ minWidth: { md: 150 }, flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
            >
              {useCaseOptions.map((u) => (
                <MenuItem key={u.value || 'all'} value={u.value}>
                  {u.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: { xs: '100%', md: 'auto' }, '& > *': { flex: { xs: 1, md: 'none' } } }}
          >
            <Button variant="contained" onClick={() => handleSearch({ reset: false })}>
              {isLoading ? '...' : 'Buscar'}
            </Button>
            <Button onClick={() => handleSearch({ reset: true })}>Limpiar</Button>
          </Stack>
        </>
      )}

      {
        numSelected > 0 ? (
          <Tooltip title="Delete">
            <IconButton>
              <Iconify icon="eva:trash-2-fill" />
            </IconButton>
          </Tooltip>
        ) : null
        //   (
        //     <Tooltip title="Filter list">
        //       <IconButton>
        //         <Iconify icon="ic:round-filter-list" />
        //       </IconButton>
        //     </Tooltip>
        //   )
      }
    </RootStyle>
  );
}
